import React, { useState, useEffect } from 'react';
import {
    Modal,
    Form,
    Input,
    DatePicker,
    Switch,
    Button,
    Space,
    Table,
    Popconfirm,
    Tag,
    Image,
    Tooltip,
    Row,
    Col,
    Statistic,
    Alert
} from 'antd';
import { apiMethods, Media } from '../services/api';
import dayjs from '../config/dayjs';
import { Typography } from 'antd';
import { UploadOutlined, DeleteOutlined, InboxOutlined, RedoOutlined, EditOutlined } from '@ant-design/icons';
import ScheduleGrid from './ScheduleGrid';

const { Title } = Typography;

interface MediaComponentProps {
    className?: string;
}

const MediaComponent: React.FC<MediaComponentProps> = ({ className = '' }) => {
    const [media, setMedia] = useState<Media[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');
    const [uploadForm, setUploadForm] = useState<{
        title: string;
        description: string;
        publishDate: string;
        unpublishDate: string;
        isActive: boolean;
        displayDuration: number;
        visibilitySchedule: { [day: string]: number[] } | null;
    }>({
        title: '',
        description: '',
        publishDate: '',
        unpublishDate: '',
        isActive: true,
        displayDuration: 20,
        visibilitySchedule: null,
    });
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState<string>('');
    const [actionStatus, setActionStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [mediaSummary, setMediaSummary] = useState<any>(null);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editMedia, setEditMedia] = useState<Media | null>(null);
    const [editForm, setEditForm] = useState<{
        title: string;
        description: string;
        publishDate: string;
        unpublishDate: string;
        isActive: boolean;
        displayDuration: number;
        visibilitySchedule: { [day: string]: number[] } | null;
    }>({
        title: '',
        description: '',
        publishDate: '',
        unpublishDate: '',
        isActive: true,
        displayDuration: 20,
        visibilitySchedule: null,
    });
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [validationStatus, setValidationStatus] = useState<{
        [filename: string]: {
            status: 'pending' | 'validating' | 'valid' | 'invalid';
            message?: string;
            details?: any;
        };
    }>({});

    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

    useEffect(() => {
        reloadAllMedia();
    }, [activeTab]);

    const reloadAllMedia = async () => {
        try {
            setLoading(true);
            const data = await apiMethods.getMedia();
            setMedia(data);
            await loadSummary();
        } catch (error) {
            setActionStatus({ type: 'error', message: 'Failed to reload media.' });
        } finally {
            setLoading(false);
        }
    };

    const loadSummary = async () => {
        try {
            const summary = await apiMethods.getMediaStatusSummary();
            setMediaSummary(summary);
        } catch (error) {
            setMediaSummary(null);
        }
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files[0];
            setSelectedFiles([file]);
            // Set to 'validating' and update immediately
            const initialStatus: typeof validationStatus = {};
            if (file.type.startsWith('image/')) {
                initialStatus[file.name] = { status: 'validating' };
            } else {
                initialStatus[file.name] = { status: 'valid' };
            }
            setValidationStatus(initialStatus);
            // Now validate the file and update status as it completes
            const newValidationStatus: typeof validationStatus = { ...initialStatus };
            if (file.type.startsWith('image/')) {
                try {
                    const validationResult = await apiMethods.validateImage(file, true, true);
                    newValidationStatus[file.name] = {
                        status: validationResult.validation_result.is_acceptable ? 'valid' : 'invalid',
                        message: validationResult.validation_result.is_acceptable 
                            ? 'Image meets A4 requirements' 
                            : validationResult.validation_result.rejection_reasons.join(', '),
                        details: validationResult.validation_result
                    };
                } catch (error: any) {
                    newValidationStatus[file.name] = {
                        status: 'invalid',
                        message: error.message || 'Validation failed'
                    };
                }
                setValidationStatus({ ...newValidationStatus });
            }
        }
    };

    const handleCloseUploadModal = () => {
        setShowUploadModal(false);
        setUploadError(null);
        setSelectedFiles([]);
        setValidationStatus({});
        setUploadForm({
            title: '',
            description: '',
            publishDate: '',
            unpublishDate: '',
            isActive: true,
            displayDuration: 20,
            visibilitySchedule: null,
        });
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) return;

        setUploading(true);
        setUploadError(null); // Clear any previous error
        
        try {
            for (const file of selectedFiles) {
                // Validate image files before upload
                if (file.type.startsWith('image/')) {
                    try {
                        console.log(`Validating image: ${file.name}`);
                        const validationResult = await apiMethods.validateImage(file, true, true);
                        
                        if (!validationResult.validation_result.is_acceptable) {
                            const reasons = validationResult.validation_result.rejection_reasons.join(', ');
                            throw new Error(`Image validation failed for ${file.name}: ${reasons}`);
                        }
                        
                        console.log(`Image validation passed for ${file.name}`);
                    } catch (validationError: any) {
                        console.error(`Validation error for ${file.name}:`, validationError);
                        throw new Error(validationError.message || `Failed to validate ${file.name}`);
                    }
                }
                
                const formData = new FormData();
                formData.append('file', file);
                formData.append('title', uploadForm.title || file.name);
                formData.append('description', uploadForm.description);
                formData.append('publish_date', uploadForm.publishDate);
                formData.append('unpublish_date', uploadForm.unpublishDate);
                formData.append('is_active', uploadForm.isActive.toString());
                formData.append('display_duration', uploadForm.displayDuration.toString());
                formData.append('visibility_schedule', JSON.stringify(uploadForm.visibilitySchedule));

                await apiMethods.uploadMedia(formData);
            }

            // Success - show success message and close modal
            setActionStatus({ type: 'success', message: `Successfully uploaded ${selectedFiles.length} file(s)` });
            setSelectedFiles([]);
            setUploadForm({
                title: '',
                description: '',
                publishDate: '',
                unpublishDate: '',
                isActive: true,
                displayDuration: 20,
                visibilitySchedule: null,
            });
            setShowUploadModal(false);
            reloadAllMedia();
        } catch (error: any) {
            console.error('Error uploading files:', error);
            console.error('Error response:', error?.response);
            console.error('Error response data:', error?.response?.data);
            
            // Extract detailed error message with multiple fallbacks
            let errorMessage = 'Failed to upload files';
            
            if (error?.response?.data) {
                // Try different possible error message fields
                if (typeof error.response.data === 'string') {
                    errorMessage = error.response.data;
                } else if (error.response.data.detail) {
                    errorMessage = error.response.data.detail;
                } else if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.data.error) {
                    errorMessage = error.response.data.error;
                } else {
                    // If data is an object but no standard fields, stringify it
                    errorMessage = JSON.stringify(error.response.data);
                }
            } else if (error?.message) {
                errorMessage = error.message;
            }
            
            // Set upload error to show in modal - don't close the modal
            setUploadError(errorMessage);
            setSelectedFiles([]); // Clear selected files after error so user can retry
        } finally {
            setUploading(false);
        }
    };

    const handleArchiveExpired = async () => {
        try {
            const result = await apiMethods.cleanupExpiredMedia('archive_expired');
            setActionStatus({
                type: 'success',
                message: `Archived expired media: ${result.processed} files processed`
            });
            reloadAllMedia();
        } catch (error) {
            console.error('Error archiving expired media:', error);
            setActionStatus({ type: 'error', message: 'Failed to archive expired media' });
        }
    };

    const handleDeleteArchived = async () => {
        if (!window.confirm('Are you sure you want to permanently delete all archived media?')) return;
        try {
            const result = await apiMethods.deleteArchivedMedia();
            setActionStatus({
                type: 'success',
                message: `Deleted archived media: ${result.processed} files processed`
            });
            reloadAllMedia();
        } catch (error) {
            console.error('Error deleting archived media:', error);
            setActionStatus({ type: 'error', message: 'Failed to delete archived media' });
        }
    };

    // Filter media for each tab
    const activeMedia = media.filter(item => !item.is_archived);
    const archivedMediaList = media.filter(item => item.is_archived);

    const handleArchive = async (mediaId: string) => {
        try {
            await apiMethods.archiveSelectedMedia([mediaId]);
            setActionStatus({ type: 'success', message: 'Media archived successfully.' });
            reloadAllMedia();
        } catch (error) {
            setActionStatus({ type: 'error', message: 'Failed to archive media.' });
        }
    };

    const handleUnarchive = async (mediaId: string) => {
        try {
            await apiMethods.restoreSelectedMedia([mediaId]);
            setActionStatus({ type: 'success', message: 'Media unarchived successfully.' });
            reloadAllMedia();
        } catch (error) {
            setActionStatus({ type: 'error', message: 'Failed to unarchive media.' });
        }
    };

    const openEditModal = (item: Media) => {
        setEditMedia(item);
        setEditForm({
            title: item.title || '',
            description: item.description || '',
            publishDate: item.publish_date ? dayjs(item.publish_date).format('YYYY-MM-DD') : '',
            unpublishDate: item.unpublish_date ? dayjs(item.unpublish_date).format('YYYY-MM-DD') : '',
            isActive: item.is_active,
            displayDuration: item.display_duration || 20,
            visibilitySchedule: item.visibility_schedule || null,
        });
        setEditModalVisible(true);
    };

    const handleEditFormChange = (field: string, value: any) => {
        setEditForm(prev => ({ ...prev, [field]: value }));
    };

    const handleEditSave = async () => {
        if (!editMedia) return;
        try {
            // Map isActive to is_active for backend compatibility
            const payload = { ...editForm, is_active: editForm.isActive, visibility_schedule: editForm.visibilitySchedule } as any;
            delete payload.isActive;
            delete payload.visibilitySchedule;
            await apiMethods.updateMedia(editMedia._id, payload);
            setEditModalVisible(false);
            setEditMedia(null);
            setActionStatus({ type: 'success', message: 'Media updated successfully' });
            reloadAllMedia();
        } catch (error) {
            console.error('Error updating media:', error);
            setActionStatus({ type: 'error', message: 'Failed to update media' });
        }
    };

    const columns = [
        {
            title: 'Preview',
            dataIndex: '_id',
            key: 'preview',
            render: (_id: string, record: Media) => {
                const mediaUrl = `${API_BASE_URL}/media/${record._id}/display`;
                if (record.mime_type.startsWith('image/')) {
                    return (
                        <Image
                            src={mediaUrl}
                            alt={record.title || record.file_name}
                            style={{ maxWidth: '100px', maxHeight: '100px', cursor: 'pointer' }}
                            preview={{
                                visible: showImageModal && selectedImage === mediaUrl,
                                onVisibleChange: (visible) => {
                                    if (!visible) {
                                        setSelectedImage('');
                                    }
                                    setShowImageModal(visible);
                                },
                                src: selectedImage
                            }}
                            onClick={() => {
                                setSelectedImage(mediaUrl);
                                setShowImageModal(true);
                            }}
                        />
                    );
                } else if (record.mime_type.startsWith('video/')) {
                    return (
                        <video
                            src={mediaUrl}
                            controls
                            style={{ maxWidth: '100px', maxHeight: '100px', cursor: 'pointer' }}
                        >
                            Your browser does not support the video tag.
                        </video>
                    );
                } else {
                    return <span>Unsupported</span>;
                }
            }
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (text: string, record: Media) => (
                <Tooltip title={record.description}>
                    <span>{text || record.file_name}</span>
                </Tooltip>
            )
        },
        {
            title: 'Publish Date',
            dataIndex: 'publish_date',
            key: 'publish_date',
            render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : 'N/A'
        },
        {
            title: 'Unpublish Date',
            dataIndex: 'unpublish_date',
            key: 'unpublish_date',
            render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : 'N/A'
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'status',
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Active' : 'Inactive'}</Tag>
            )
        },
        {
            title: 'Display Duration',
            dataIndex: 'display_duration',
            key: 'display_duration',
            render: (duration: number) => (
                <span>{duration || 20}s</span>
            )
        },
        // TODO: Re-enable after fixing backend deployment
        // {
        //     title: 'Padding Analysis',
        //     dataIndex: 'padding_analysis',
        //     key: 'padding_analysis',
        //     render: (paddingAnalysis: any, record: Media) => {
        //         // Temporarily disabled
        //         return <span>Coming Soon</span>;
        //     }
        // },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: Media) => (
                <Space size="middle">
                    <Tooltip title="Edit">
                        <Button icon={<EditOutlined />} onClick={() => openEditModal(record)} />
                    </Tooltip>
                    {activeTab === 'active' ? (
                        <Popconfirm
                            title="Are you sure you want to archive this item?"
                            onConfirm={() => handleArchive(record._id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Tooltip title="Archive">
                                <Button danger icon={<InboxOutlined />} />
                            </Tooltip>
                        </Popconfirm>
                    ) : (
                        <Popconfirm
                            title="Are you sure you want to unarchive this item?"
                            onConfirm={() => handleUnarchive(record._id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Tooltip title="Unarchive">
                                <Button icon={<RedoOutlined />} />
                            </Tooltip>
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ];

    // const allSelected = selectedMediaIds.length === allMedia.length && allMedia.length > 0;

    return (
        <div className={`p-4 bg-white text-gray-800 ${className}`}>
            <Title level={2} style={{ color: '#1f2937' }}>Media Management</Title>

            {actionStatus && (
                <Alert
                    message={actionStatus.message}
                    type={actionStatus.type}
                    showIcon
                    closable
                    onClose={() => setActionStatus(null)}
                    className="mb-4"
                />
            )}

            {mediaSummary && (
                <div className="mb-4 p-4 border border-gray-200 rounded-lg">
                    <Row gutter={[16, 16]}>
                        <Col xs={12} sm={8} md={4}>
                            <Statistic 
                                title="Total" 
                                value={mediaSummary.total_count ?? 0} 
                                suffix={`(${(mediaSummary.total_size_mb ?? 0).toFixed(2)} MB)`} 
                            />
                        </Col>
                        <Col xs={12} sm={8} md={3}><Statistic title="Active" value={mediaSummary.active_count ?? 0} /></Col>
                        <Col xs={12} sm={8} md={3}><Statistic title="Inactive" value={mediaSummary.inactive_count ?? 0} /></Col>
                        <Col xs={12} sm={8} md={3}><Statistic title="Expired" value={mediaSummary.expired_count ?? 0} /></Col>
                        <Col xs={12} sm={8} md={4}><Statistic title="Expiring Soon" value={mediaSummary.expiring_soon_count ?? 0} /></Col>
                        <Col xs={12} sm={8} md={4}>
                             <Statistic 
                                title="Archived" 
                                value={mediaSummary.archived_count ?? 0} 
                                suffix={`(${(mediaSummary.archived_size_mb ?? 0).toFixed(2)} MB)`} 
                            />
                        </Col>
                    </Row>
                </div>
            )}

            <Space className="mb-4">
                <Button type="primary" icon={<UploadOutlined />} onClick={() => {
                    setShowUploadModal(true);
                    setUploadError(null); // Clear any previous errors
                }}>
                    Upload Media
                </Button>
                {/* TODO: Re-enable after fixing backend deployment */}
                {/* <Button
                    icon={<ScanOutlined />}
                    onClick={handleAnalyzeAllPadding}
                    disabled={loading}
                >
                    Analyze All Images
                </Button> */}
                <Button
                    danger
                    icon={<InboxOutlined />}
                    onClick={handleArchiveExpired}
                    disabled={loading}
                >
                    Archive Expired
                </Button>
                <Button
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleDeleteArchived}
                    disabled={loading || archivedMediaList.length === 0}
                >
                    Delete All Archived
                </Button>
            </Space>

            <div className="flex border-b border-gray-200 mb-4">
                <button
                    className={`py-2 px-4 text-lg ${activeTab === 'active' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500'}`}
                    onClick={() => setActiveTab('active')}
                >
                    Active Media ({activeMedia.length})
                </button>
                <button
                    className={`py-2 px-4 text-lg ${activeTab === 'archived' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500'}`}
                    onClick={() => setActiveTab('archived')}
                >
                    Archived Media ({archivedMediaList.length})
                </button>
            </div>
{/* 
            <Checkbox
                onChange={(e) => {
                    if (e.target.checked) {
                        setSelectedMediaIds(allMedia.map(item => item._id));
                    } else {
                        setSelectedMediaIds([]);
                    }
                }}
                checked={allSelected}
                disabled={allMedia.length === 0}
            >
                Select All
            </Checkbox> */}

            <Table
                columns={columns}
                dataSource={activeTab === 'active' ? activeMedia : archivedMediaList}
                loading={loading}
                rowKey="_id"
            />

            <Modal
                title="Upload Media"
                open={showUploadModal}
                onCancel={handleCloseUploadModal}
                footer={[
                    <Button key="back" onClick={handleCloseUploadModal}>
                        Cancel
                    </Button>,
                    <Button 
                        key="submit" 
                        type="primary" 
                        loading={uploading} 
                        onClick={handleUpload}
                        disabled={Object.values(validationStatus).some(status => status.status === 'invalid' || status.status === 'validating')}
                    >
                        Upload
                    </Button>,
                ]}
            >
                {uploadError && (
                    <Alert
                        message="Upload Failed"
                        description={uploadError}
                        type="error"
                        showIcon
                        closable
                        onClose={() => setUploadError(null)}
                        className="mb-4"
                    />
                )}
                
                <Form layout="vertical">
                    <input type="file" onChange={handleFileSelect} className="mb-4" />
                    
                    {/* Validation Status Display */}
                    {selectedFiles.length > 0 && (
                        <div className="mb-4">
                            <h4>File Validation Status:</h4>
                            {selectedFiles.map((file) => {
                                const status = validationStatus[file.name];
                                if (!status) return null;
                                
                                const isImage = file.type.startsWith('image/');
                                const statusColor = status.status === 'valid' ? 'green' : 
                                                  status.status === 'invalid' ? 'red' : 
                                                  status.status === 'validating' ? 'blue' : 'default';
                                
                                return (
                                    <div key={file.name} className="mb-2 p-2 border rounded">
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium">{file.name}</span>
                                            <Tag color={statusColor}>
                                                {status.status === 'validating' ? 'Validating...' :
                                                 status.status === 'valid' ? 'Valid' :
                                                 status.status === 'invalid' ? 'Invalid' : 'Pending'}
                                            </Tag>
                                        </div>
                                        {status.message && (
                                            <div className="text-sm mt-1">
                                                {status.status === 'valid' ? (
                                                    <span className="text-green-600">{status.message}</span>
                                                ) : status.status === 'invalid' ? (
                                                    <span className="text-red-600">{status.message}</span>
                                                ) : (
                                                    <span className="text-blue-600">{status.message}</span>
                                                )}
                                            </div>
                                        )}
                                        {isImage && status.details && (
                                            <div className="text-xs text-gray-500 mt-1">
                                                <div>Aspect Ratio: {status.details.basic_info?.aspect_ratio}</div>
                                                <div>A4-like: {status.details.a4_check?.is_a4_like ? 'Yes' : 'No'}</div>
                                                <div>Has Padding: {status.details.padding_check?.has_padding ? 'Yes' : 'No'}</div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    
                    <Form.Item label="Title">
                        <Input value={uploadForm.title} onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })} />
                    </Form.Item>
                    <Form.Item label="Description">
                        <Input.TextArea value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} />
                    </Form.Item>
                    <Form.Item label="Publish Date">
                        <DatePicker onChange={(_, dateString) => setUploadForm({ ...uploadForm, publishDate: dateString as string })} />
                    </Form.Item>
                    <Form.Item label="Unpublish Date">
                        <DatePicker onChange={(_, dateString) => setUploadForm({ ...uploadForm, unpublishDate: dateString as string })} />
                    </Form.Item>
                    <Form.Item label="Display Duration (seconds)" tooltip="How long this media should be displayed in the carousel">
                        <Input 
                            type="number" 
                            min={1} 
                            max={60} 
                            value={uploadForm.displayDuration} 
                            onChange={(e) => setUploadForm({ ...uploadForm, displayDuration: parseInt(e.target.value) || 20 })} 
                            placeholder="Duration in seconds (default: 20)"
                        />
                    </Form.Item>
                    <Form.Item label="Is Active" valuePropName="checked">
                        <Switch checked={uploadForm.isActive} onChange={(checked) => setUploadForm({ ...uploadForm, isActive: checked })} />
                    </Form.Item>                    
                    <Form.Item label="Visibility Schedule">
                        <ScheduleGrid
                            value={uploadForm.visibilitySchedule}
                            onChange={schedule => setUploadForm({ ...uploadForm, visibilitySchedule: schedule })}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Edit Modal */}
            <Modal
                title="Edit Media"
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                onOk={handleEditSave}
            >
                <Form layout="vertical">
                    <Form.Item label="Title">
                        <Input value={editForm.title} onChange={(e) => handleEditFormChange('title', e.target.value)} />
                    </Form.Item>
                    <Form.Item label="Description">
                        <Input.TextArea value={editForm.description} onChange={(e) => handleEditFormChange('description', e.target.value)} />
                    </Form.Item>
                    <Form.Item label="Publish Date">
                        <DatePicker
                            value={editForm.publishDate ? dayjs(editForm.publishDate) : null}
                            onChange={(_, dateString) => handleEditFormChange('publishDate', dateString as string)}
                        />
                    </Form.Item>
                    <Form.Item label="Unpublish Date">
                        <DatePicker
                            value={editForm.unpublishDate ? dayjs(editForm.unpublishDate) : null}
                            onChange={(_, dateString) => handleEditFormChange('unpublishDate', dateString as string)}
                        />
                    </Form.Item>
                    <Form.Item label="Display Duration (seconds)" tooltip="How long this media should be displayed in the carousel">
                        <Input 
                            type="number" 
                            min={1} 
                            max={60} 
                            value={editForm.displayDuration} 
                            onChange={(e) => handleEditFormChange('displayDuration', parseInt(e.target.value) || 20)} 
                            placeholder="Duration in seconds (default: 20)"
                        />
                    </Form.Item>
                    <Form.Item label="Is Active">
                        <Switch
                            checked={editForm.isActive}
                            onChange={(checked) => handleEditFormChange('isActive', checked)}
                        />
                    </Form.Item>
                    <Form.Item label="Visibility Schedule">
                        <ScheduleGrid
                            value={editForm.visibilitySchedule}
                            onChange={schedule => handleEditFormChange('visibilitySchedule', schedule)}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default MediaComponent; 